// Renderer orchestration contract (§24.1). Frozen after WP0: additive changes only. WP7 owns the
// implementation. `render(view)` runs, in order:
//   1. the governor samples the frame time;
//   2. VFX build the presented view (hitstop-held poses, slow-motion dt) that every later step uses;
//   3. mapView.update; 4. beanRigs[slot].update; 5. entityViews.update; 6. vfx.update;
//   7. camera.update (with the VFX moment: final-KO close-up, GAME! orbit); 8. post.render, with the
//      text-pop overlay on top and the per-render rig overrides (hit flash, BONK CAM killer outline).
import {
  ACESFilmicToneMapping, AdditiveBlending, AgXToneMapping, BufferGeometry, NeutralToneMapping, Color, DirectionalLight, Float32BufferAttribute, FogExp2, Group, HemisphereLight, Mesh,
  PCFShadowMap, PMREMGenerator, Plane, Raycaster, Scene, ShaderMaterial, SRGBColorSpace, Vector2, Vector3, WebGLRenderer, type Object3D,
} from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { MAX_BEANS, NO_SLOT, VOID } from '@shared/constants';
import type { Cosmetics } from '@shared/cosmetics';
import { EV } from '@shared/events';
import { Cause, RoomPhase, type MapId, type ModeId } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import type { RoomStateDTO } from '@shared/protocol/messages';
import type { Vec2 } from '@shared/types';
import { onSim, type Bus } from '../core/bus';
import { createFrameView, type BeanView, type FrameView } from '../game/FrameView';
import { loadProfile } from '../profile/Profile';
import type { Settings } from '../profile/settings';
import { ActorLift } from './actorLift';
import { createBeanRig, type BeanRig } from './bean/BeanRig';
import { createLockerStage, type LockerStage } from './bean/LockerStage';
import { createCameraDirector, type GameCamera } from './camera/CameraDirector';
import type { HudInsets } from './camera/framing';
import { createBlobShadows, type BlobShadows } from './entities/BlobShadows';
import { createEntityViews, type EntityViews } from './entities/EntityViews';
import { createMapView, type MapView } from './maps/MapView';
import { createMapProbe, type MapProbe } from './maps/probe';
import { createSolidsOverlay, type SolidsOverlay } from './maps/SolidsOverlay';
import { createMaterialLibrary, type BasicMaterialLibrary } from './materials/library';
import { createPostChain, type GamePost, type PostFrame } from './post/PostChain';
import { lowerTier, QualityGovernor, TIERS } from './quality';
import { VhsOverlay } from './replay/VhsOverlay';
import { spriteAtlas } from './vfx/atlas';
import { popAtlas } from './vfx/TextPops';
import { identityHex } from './vfx/effects';
import { createVfxDirector, type GameVfx } from './vfx/VfxDirector';

export type QualityTier = 'high' | 'medium' | 'low' | 'potato';

export interface Renderer {
  init(canvas: HTMLCanvasElement, settings: Settings): Promise<void>;
  setStage(stage: 'game' | 'locker'): void;
  setRound(info: { mapId: MapId; modeId: ModeId; seed: number; golden: boolean }): void;   // (re)builds MapView, resets VFX & rigs
  render(view: FrameView): void;
  setQuality(tier: QualityTier): void;
  worldToScreen(x: number, y: number, z: number, out: { x: number; y: number; visible: boolean }): void;   // sim coords → CSS px
  groundFromScreen(sx: number, sy: number, out: Vec2): boolean;                                          // CSS px → sim ground plane
  locker: { setCosmetics(c: Cosmetics, identity: number): void; spin(dx: number): void };
  renderPortrait(c: Cosmetics, identity: number, size: number): HTMLCanvasElement;                     // thumbnails (awards, locker cards)
  readonly stats: { fps: number; frameMs: number; drawCalls: number; triangles: number; tier: QualityTier };
}

export interface RendererOptions {
  bus: Bus;
  /** Initial tier (§24.3, chosen at boot). */
  tier: QualityTier;
  /** `?quality=` was given: the tier is fixed and the Auto governor never runs. */
  forcedQuality: boolean;
  /** `?nopost=1`: the Potato post chain while keeping at most Low geometry (§24.3). */
  nopost: boolean;
  skipIntro: boolean;
  /** `?debug=solids`: the sim solids and walkable boundaries drawn over the map (§28). */
  solidsOverlay: boolean;
}

/** The WP0 renderer plus what boot and debug hooks need beyond the §24.1 interface. */
export interface GameRenderer extends Renderer {
  /** Frames rendered since the last `setRound` (debug `screenshotReady`). */
  readonly framesSinceRound: number;
  readonly mapReady: boolean;
  /** WP7 internals for verification pages and tests (additive). */
  readonly vfx: GameVfx | null;
  readonly cameraDirector: GameCamera | null;
  readonly post: GamePost | null;
  /**
   * Additive (INT-B): the HUD's footprint over the game view (viewport fractions; null = the shipped
   * HUD's defaults). Gameplay framing keeps beans clear of it and centers play in the free band.
   */
  setHudInsets(h: HudInsets | null): void;
  /**
   * Additive (INT-B): the frame the last render actually drew — after hitstop holds, slow motion
   * and the actor lift onto tilted floors (Teacup saucer, SPUN beans in the cup). World-space UI
   * (nameplates, prompts, arrows) should place itself from this, not from the source frame.
   */
  readonly presented: FrameView;
  /**
   * Additive (INT-B): the UI's laid-out nameplates, flat [centerX, bottomY, width, height] CSS px
   * per visible plate, reported after each nameplate layout; text pops keep clear of them. null clears.
   */
  setNameplateRects(rects: ArrayLike<number> | null): void;
}

/** Test hook (`window.__SHOVEOFF_RENDER__`): presented state for the WP7 browser checks. */
export interface RenderDebug {
  presented(slot: number): { x: number; y: number; z: number; vx: number; vy: number; vz: number; state: number; active: boolean; frozenMs: number } | null;
  frame(): { now: number; source: string; local: number; timeScale: number; shot: string; moment: string | null; replay: string | null; t: number; camDist: number; trauma: number; phase: number; roundTick: number; floorTiltDeg: number };
  fxLog(): { ev: number; tag: string }[];
  /** VFX tags logged since the first `n` ever (`total` for the next call); survives the log's wrap. */
  fxSince(n: number): { total: number; tags: string[] };
  fxCount(): { particles: number; decals: number; pops: number };
  pops(): { word: number; x: number; y: number; z: number; slot: number; age: number; priority: number; sx: number; sy: number }[];
  /** Nameplate rects the pops kept clear of in the last layout: [centerX, centerY, w, h] CSS px. */
  popBlocks(): number[][];
  /** Telegraph decals drawn last frame by kind, plus `guide` (1 when the push guide showed). */
  telegraphs(): Record<string, number>;
  postInfo(): { tier: string; final: boolean; game: number; vhs: number; wipe: number; letterbox: number };
  /** Adds `n` full-screen layers of expensive fragment work (synthetic GPU load for the governor check). */
  synthLoad(n: number): void;
  tier(): string;
  /** Starts recording the presented pose of `slot` every frame (at most `max` samples). */
  trace(slot: number, max?: number): void;
  /** The recorded samples: [now ms, x, y, z, vx, vy, vz, frozenMs, state, active, rawX, rawY, rawZ, replay]. */
  traceData(): number[][];
  /** Approximate triangles per top-level scene object (rigs, map, VFX…). */
  breakdown(): Record<string, number>;
  /** Sim point → CSS px through the current camera. */
  project(x: number, y: number, z: number): { x: number; y: number; visible: boolean };
  /** Live tuning for the visual review loop. */
  tuneBloom(threshold: number, strength: number, radius: number): void;
  /** Mean main-thread ms per frame of each render step (exponential average). */
  timings(): Record<string, number>;
  /** Turns a layer off for cost measurements: 'vfx' | 'vignette' | 'overlay' | 'map' | 'beans' | 'entities' | 'shadows' | 'sun'; 'potatoTarget' on = the old Potato path. */
  toggle(name: string, on: boolean): void;
  /** Name paths of the map view's parts down to `depth` levels (for `toggle('map:<path>')`). */
  mapParts(depth?: number): string[];
  /**
   * Live look tuning for the art review: 'exposure', 'fog' (density), 'hemi', 'sun', 'toneMapping'
   * (0 Neutral, 1 ACES, 2 AgX), or 'grade.<preset>.<field>' (lift, gamma, gain, sat, contrast, tintAmt).
   */
  tune(name: string, value: number): void;
  /**
   * Perf stress (verification): fires `perSec` synthetic heavy VFX events per second around the
   * arena (explosions, hits, clashes, heavy landings) on top of live play; 0 stops.
   */
  stress(perSec: number): void;
  /** PNG data URLs of the VFX sprite atlas (channel masks) and the text-pop atlas, for sprite review. */
  atlases(): { sprites: string; words: string };
}

declare global {
  interface Window { __SHOVEOFF_RENDER__?: RenderDebug }
}

const KEY_LIGHT = { color: '#FFF2DE', intensity: 2.2, dir: new Vector3(-0.5, 1, 0.6).normalize(), distance: 40 } as const;
const HEMI_INTENSITY = 1.1;
/**
 * §12.5 gives 0.012; at the 18–32 m gameplay distances that put a 9–16 % lilac veil on the arena
 * itself and ~25 % on the void around it, which read as milky in the live review. 0.007 keeps the
 * soft depth fade on backgrounds and leaves the arena crisp.
 */
const FOG_DENSITY = 0.007;
const ENV_SIGMA = 0.04;
const SHADOW = { pad: 2, bias: -0.0005, normalBias: 0.02, depth: 100 } as const;
const STATS_ALPHA = 0.1;
const HIGH_CONTRAST_OUTLINES = 1.6;
/** §14.4 push guide Auto: on until Bean Level 5. */
const PUSH_GUIDE_LEVEL = 5;
/** Rewind tear fade-in / tail (s). */
const WIPE = { in: 0.12, tail: 0.18 } as const;
const BEAN_HEIGHT = 1.1;
/** Render steps timed for `__SHOVEOFF_RENDER__.timings()`. */
const TIMING_KEYS = ['present', 'map', 'rigs', 'entities', 'vfx', 'camera', 'shadows', 'post', 'total'] as const;

const POTATO_VIGNETTE_CSS = 'position:fixed;inset:0;pointer-events:none;z-index:0;'
  + 'background:radial-gradient(ellipse 75% 70% at 50% 48%, rgba(42,26,58,0) 58%, rgba(42,26,58,0.22) 100%);';

/** Full-screen layers of busy-work for the governor check (not visible). */
function createLoadMesh(layers: number): Group {
  const g = new Group();
  g.name = 'synthetic-load';
  for (let i = 0; i < layers; i++) g.add(loadLayer());
  return g;
}

function loadLayer(): Mesh {
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
  const mat = new ShaderMaterial({
    vertexShader: 'void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader: `uniform float uN; void main() { float a = 0.0; for (int i = 0; i < 16384; i++) { if (float(i) >= uN) break; a += sin(a * 1.37 + float(i) + gl_FragCoord.x * 0.01) * 0.5; } gl_FragColor = vec4(vec3(abs(a) * 1e-7), 0.0); }`,
    uniforms: { uN: { value: 16384 } },
    transparent: true, depthTest: false, depthWrite: false, blending: AdditiveBlending,
  });
  const mesh = new Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = 999;
  return mesh;
}

/** Triangles per scene subsystem (verification of the §12.9 budgets). */
function triangleBreakdown(scene: Scene): Record<string, number> {
  const out: Record<string, number> = {};
  for (const child of scene.children) {
    let n = 0;
    child.traverseVisible((o) => {
      const m = o as Mesh;
      if (!m.isMesh || !m.geometry) return;
      const g = m.geometry;
      const idx = g.index ? g.index.count : (g.getAttribute('position')?.count ?? 0);
      const inst = (m as unknown as { count?: number; isInstancedMesh?: boolean }).isInstancedMesh ? (m as unknown as { count: number }).count
        : (g as unknown as { instanceCount?: number }).instanceCount ?? 1;
      n += (idx / 3) * (Number.isFinite(inst) ? inst : 1);
    });
    const key = child.name || child.type;
    out[key] = (out[key] ?? 0) + Math.round(n);
  }
  return out;
}

export function createRenderer(opts: RendererOptions): GameRenderer {
  let webgl: WebGLRenderer | null = null;
  let canvas: HTMLCanvasElement | null = null;
  let settings: Settings | null = null;
  let lib: BasicMaterialLibrary | null = null;
  let camera: GameCamera | null = null;
  let post: GamePost | null = null;
  let vfx: GameVfx | null = null;
  let entities: EntityViews | null = null;
  let shadows: BlobShadows | null = null;
  let lockerStage: LockerStage | null = null;
  let mapView: MapView | null = null;
  let probe: MapProbe | null = null;
  let vhs: VhsOverlay | null = null;
  let vignette: HTMLDivElement | null = null;
  let loadMesh: Group | null = null;
  const solidsOverlay: SolidsOverlay | null = opts.solidsOverlay ? createSolidsOverlay() : null;
  const scene = new Scene();
  const rigs: BeanRig[] = [];
  const rigLooks = new Float64Array(MAX_BEANS).fill(-1);
  const hemi = new HemisphereLight('#FFFFFF', '#FFFFFF', HEMI_INTENSITY);
  const sun = new DirectionalLight(KEY_LIGHT.color, KEY_LIGHT.intensity);
  const governor = new QualityGovernor();
  const lift = new ActorLift();
  const raycaster = new Raycaster();
  const groundPlane = new Plane(new Vector3(0, 1, 0), 0);
  const tmpV3 = new Vector3();
  const winnerFeet = new Vector3();
  const postFrame: PostFrame = { focus: null, winnerFeet: null, winnerHead: null, letterbox: false, wipe: 0, tiltShift: true };
  const winnerHead = new Vector3();
  const ndc = new Vector2();
  let presentedView: FrameView = createFrameView();
  let stage: 'game' | 'locker' = 'game';
  let tier: QualityTier = opts.tier;
  let lastNow = -1;
  let cssW = 0;
  let cssH = 0;
  let sizeDirty = true;
  let framesSinceRound = 0;
  let profileLevel = 1;
  let roundInfo: { mapId: MapId; modeId: ModeId; seed: number; golden: boolean } | null = null;
  /** Geometry tier the rigs and map were built with. */
  let builtTier: QualityTier = opts.tier;
  /** Debug cost bisection: bean rigs hidden after their update. */
  let beansHidden = false;
  /** Debug: re-present the last frame unchanged (art review A/B on one still). */
  let frozen = false;
  /** Debug perf stress: synthetic VFX events per second (`stress()`), and the accumulator. */
  let stressRate = 0;
  let stressAcc = 0;
  const stressKinds = [EV.BOMB_BOOM, EV.HIT, EV.CLASH, EV.LAND, EV.HIT] as const;
  let vignetteAllowed = true;
  /** The UI's HUD footprint (setHudInsets), re-applied when the camera is (re)built. */
  let hudInsets: HudInsets | null = null;
  const timing = new Float64Array(TIMING_KEYS.length);
  let tMark = 0;
  /** Accumulates the time since the last mark into step `i`. */
  function lap(i: number): void {
    const t = performance.now();
    timing[i] += (t - tMark - timing[i]) * 0.05;
    tMark = t;
  }
  /** The room's current vote cards, shown on the Lobby's pads (WP6 `setVoteCards`). */
  let lastCards: RoomStateDTO['cards'] | null = null;
  let traceSlot = -1;
  let traceMax = 0;
  const traceRows: number[][] = [];
  const vhsState = { kind: 'instant' as const as 'instant' | 'bonkcam' | 'potm', t: 0, duration: 0, killer: null as { name: string; hex: string } | null };
  const killerInfo = { name: '', hex: '#FF5A4E' };

  const stats = { fps: 0, frameMs: 0, drawCalls: 0, triangles: 0, tier };

  function need<T>(v: T | null): T {
    if (v === null) throw new Error('Renderer used before init()');
    return v;
  }

  function governorActive(): boolean {
    return !opts.forcedQuality && settings?.graphics.quality === 'auto';
  }

  function geometryTier(t: QualityTier): QualityTier {
    return opts.nopost && (t === 'high' || t === 'medium') ? 'low' : t;
  }

  function postTier(t: QualityTier): QualityTier {
    return opts.nopost ? 'potato' : t;
  }

  function applySize(): void {
    const c = need(canvas);
    const w = Math.max(1, c.clientWidth);
    const h = Math.max(1, c.clientHeight);
    if (!sizeDirty && w === cssW && h === cssH) return;
    sizeDirty = false;
    cssW = w;
    cssH = h;
    need(post).resize(w, h, pixelRatio());
    need(camera).resize(w, h, h > w);
    // Outline pixel floor (§12.1 "chunky dark-plum outlines"): the §12.3 widths alone are 1–2 px at
    // gameplay distance; a bean's ink stays ≥ 1.5 px at 720p, 2.25 px at 1080p, 3 px at 1440p+.
    const bufH = h * pixelRatio();
    lib?.setOutlinePixels(Math.min(3, Math.max(1.5, bufH / 480)), bufH);
    need(vfx).pops.resize(w, h);
    const lockerCam = need(lockerStage).camera;
    lockerCam.aspect = w / h;
    lockerCam.updateProjectionMatrix();
  }

  function pixelRatio(): number {
    const spec = TIERS[postTier(tier)];
    const userScale = (settings?.graphics.renderScale ?? 100) / 100;
    return Math.min(globalThis.devicePixelRatio || 1, spec.maxDpr) * spec.renderScale * userScale;
  }

  function applyTier(): void {
    const gl = need(webgl);
    const spec = TIERS[geometryTier(tier)];
    const shadowsOn = spec.shadowMapSize > 0;
    if (gl.shadowMap.enabled !== shadowsOn) {
      gl.shadowMap.enabled = shadowsOn;
      // Shadow-map support is compiled into programs, so every lit material must rebuild.
      scene.traverse((o) => {
        if (!(o instanceof Mesh)) return;
        const m = o.material;
        if (Array.isArray(m)) for (const x of m) x.needsUpdate = true;
        else m.needsUpdate = true;
      });
    }
    sun.castShadow = shadowsOn;
    if (shadowsOn && sun.shadow.mapSize.x !== spec.shadowMapSize) {
      sun.shadow.mapSize.set(spec.shadowMapSize, spec.shadowMapSize);
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
    }
    need(lib).quality = geometryTier(tier);
    need(post).setTier(postTier(tier));
    need(vfx).setTier(geometryTier(tier));
    stats.tier = tier;
    sizeDirty = true;
  }

  function fitShadow(): void {
    if (!mapView) return;
    const b = mapView.bounds;
    const cx = (b.minX + b.maxX) / 2;
    const cy = (b.minY + b.maxY) / 2;
    const half = Math.max(b.maxX - b.minX, b.maxY - b.minY) / 2 + SHADOW.pad;
    sun.target.position.set(cx, 0, cy);
    sun.position.copy(KEY_LIGHT.dir).multiplyScalar(KEY_LIGHT.distance).add(sun.target.position);
    const cam = sun.shadow.camera;
    cam.left = -half;
    cam.right = half;
    cam.top = half;
    cam.bottom = -half;
    cam.near = 1;
    cam.far = SHADOW.depth;
    cam.updateProjectionMatrix();
  }

  function applySettings(s: Settings): void {
    const prevQuality = settings?.graphics.quality;
    settings = s;
    lib?.setOutlineScale(s.accessibility.highContrastOutlines ? HIGH_CONTRAST_OUTLINES : 1);
    for (const rig of rigs) rig.setColorblind?.(s.accessibility.colorblind);
    sizeDirty = true;
    // A fixed tier picked in Settings applies at once (a `?quality=` URL still wins); switching to
    // Auto keeps the current tier and hands it back to the governor.
    if (webgl && !opts.forcedQuality && prevQuality !== undefined && s.graphics.quality !== prevQuality && s.graphics.quality !== 'auto') {
      renderer.setQuality(s.graphics.quality);
    }
  }

  function pushGuideOn(): boolean {
    const mode = settings?.gameplay.pushGuide ?? 'auto';
    return mode === 'on' || (mode === 'auto' && profileLevel < PUSH_GUIDE_LEVEL);
  }

  /** Everything `setLook` depends on, packed into one number (5 bits per field) to compare without allocating. */
  function lookKey(b: BeanView): number {
    const c = b.cosmetics;
    let k = b.identity & 31;
    k = k * 32 + (b.team & 31);
    k = k * 32 + (b.isLocal ? 1 : 0);
    k = k * 32 + (c.color & 31);
    k = k * 32 + (c.pattern & 31);
    k = k * 32 + (c.hat & 31);
    return k * 32 + (c.eyes & 31);
  }

  function syncRig(slot: number, view: FrameView): void {
    const b = view.beans[slot];
    const look = lookKey(b);
    if (look !== rigLooks[slot]) {
      rigLooks[slot] = look;
      rigs[slot].setLook(b.cosmetics, b.identity, b.team, b.isLocal);
    }
    rigs[slot].setColorblind?.(settings?.accessibility.colorblind ?? false);   // WP5 hook (§12.2 palette)
    rigs[slot].update(b, view, need(vfx).hitstopMs(slot));
    if (beansHidden) rigs[slot].root.visible = false;
  }

  function sampleFrame(view: FrameView): void {
    const frameMs = lastNow < 0 ? 0 : view.now - lastNow;
    lastNow = view.now;
    if (frameMs <= 0) return;
    stats.frameMs += (frameMs - stats.frameMs) * STATS_ALPHA;
    stats.fps = stats.frameMs > 0 ? 1000 / stats.frameMs : 0;
    if (governorActive() && view.roomPhase === RoomPhase.PLAY && view.source !== 'replay' && tier !== 'potato' && governor.sample(frameMs)) {
      // The 'quality' event this emits shows the §24.3 toast (main.ts).
      renderer.setQuality(lowerTier(tier));
    }
  }

  /** Replay look: VHS shader mode, the rewind tear and the DOM overlay (§11, §12.8). */
  function replayLook(view: FrameView): number {
    const rp = view.replay;
    const p = need(post);
    p.setVhs(rp !== null);
    if (!rp) {
      vhs?.update(null);
      return 0;
    }
    let killer: { name: string; hex: string } | null = null;
    if (rp.kind === 'bonkcam' && rp.killerSlot !== NO_SLOT && rp.killerSlot < MAX_BEANS) {
      const k = view.beans[rp.killerSlot];
      if (k && k.active && k.name) {
        killerInfo.name = k.name;
        killerInfo.hex = identityHex(k.identity, settings?.accessibility.colorblind ?? false);
        killer = killerInfo;
      }
    }
    vhsState.kind = rp.kind;
    vhsState.t = rp.t;
    vhsState.duration = rp.duration;
    vhsState.killer = killer;
    vhs?.update(vhsState);
    return rp.t < 0 ? Math.min(1, -rp.t / WIPE.in) : Math.max(0, 1 - rp.t / WIPE.tail);
  }

  /** GAME!: the winner the desaturation keeps in color. */
  function gameSubject(view: FrameView): boolean {
    const w = need(vfx).winner;
    if (view.grade !== 'game' || w === NO_SLOT || w >= MAX_BEANS) return false;
    const b = view.beans[w];
    if (!b || !b.active) return false;
    winnerFeet.set(b.x, b.z, b.y);
    winnerHead.set(b.x, b.z + BEAN_HEIGHT * (b.big ? 1.6 : 1), b.y);
    return true;
  }

  /** Debug perf stress: synthetic heavy VFX events around the arena (`__SHOVEOFF_RENDER__.stress`). */
  function stressTick(view: FrameView, fxd: GameVfx): void {
    stressAcc += stressRate * Math.min(0.1, Math.max(0, view.dt));
    const c = MAPS[view.mapId].center;
    while (stressAcc >= 1) {
      stressAcc -= 1;
      const type = stressKinds[Math.floor(Math.random() * stressKinds.length)];
      const a = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * 6;
      let x = c.x + Math.cos(a) * r;
      let y = c.y + Math.sin(a) * r;
      let actor = 0;
      let target = 1;
      if (type !== EV.BOMB_BOOM) {
        // Hits, clashes and landings happen at beans: pick two living ones.
        const live: number[] = [];
        for (const b of view.beans) if (b.active && !b.out && !b.ghost) live.push(b.slot);
        if (live.length < 2) continue;
        actor = live[Math.floor(Math.random() * live.length)];
        target = live[(live.indexOf(actor) + 1 + Math.floor(Math.random() * (live.length - 1))) % live.length];
        x = view.beans[actor].x;
        y = view.beans[actor].y;
      }
      fxd.onSim({ type, tick: view.renderTick, a: actor, b: target, c: Cause.SHOVE, d: 60 + Math.random() * 120, x, y }, false, view);
    }
  }

  function installDebugHook(): void {
    if (typeof window === 'undefined') return;
    const hook: RenderDebug = {
      presented(slot) {
        const b = presentedView.beans[slot];
        if (!b) return null;
        return { x: b.x, y: b.y, z: b.z, vx: b.vx, vy: b.vy, vz: b.vz, state: b.state, active: b.active, frozenMs: vfx?.hitstopMs(slot) ?? 0 };
      },
      frame() {
        const v = presentedView;
        return {
          now: v.now, source: v.source, local: v.localSlot, timeScale: vfx?.timeScale ?? 1, shot: camera?.shot ?? '',
          moment: vfx?.moment?.kind ?? null, replay: v.replay?.kind ?? null, t: v.replay?.t ?? 0,
          camDist: camera ? camera.camera.position.distanceTo(camera.target) : 0, trauma: camera?.trauma ?? 0,
          phase: v.roomPhase, roundTick: v.roundTick, floorTiltDeg: Math.atan(Math.hypot(lift.gx, lift.gy)) * (180 / Math.PI),
        };
      },
      fxLog: () => vfx?.log.all() ?? [],
      fxSince: (n) => ({ total: vfx?.log.total ?? 0, tags: vfx?.log.since(n) ?? [] }),
      fxCount: () => ({ particles: vfx?.particles.count ?? 0, decals: vfx?.decals.count ?? 0, pops: vfx?.pops.count ?? 0 }),
      pops: () => vfx?.pops.snapshot() ?? [],
      popBlocks: () => vfx?.pops.plateBlocks() ?? [],
      telegraphs: () => ({ ...(vfx?.telegraphs.drawn ?? {}), guide: vfx?.guide.last ? 1 : 0 }),
      postInfo() {
        const u = post?.uniforms;
        return {
          tier: post?.tier ?? '', final: post?.finalActive ?? false, game: u?.uGame.value ?? 0, vhs: u?.uVhs.value ?? 0,
          wipe: u?.uWipe.value ?? 0, letterbox: u?.uLetter.value ?? 0,
        };
      },
      synthLoad(n) {
        if (loadMesh) {
          scene.remove(loadMesh);
          loadMesh.traverse((o) => {
            if (o instanceof Mesh) {
              o.geometry.dispose();
              (o.material as ShaderMaterial).dispose();
            }
          });
          loadMesh = null;
        }
        if (n > 0) {
          loadMesh = createLoadMesh(n);
          scene.add(loadMesh);
        }
      },
      tier: () => tier,
      trace(slot, max = 20000) {
        traceSlot = slot;
        traceMax = max;
        traceRows.length = 0;
      },
      traceData: () => traceRows,
      breakdown: () => triangleBreakdown(scene),
      project(x, y, z) {
        const o = { x: 0, y: 0, visible: false };
        renderer.worldToScreen(x, y, z, o);
        return o;
      },
      tuneBloom: (t, s, r) => post?.tuneBloom(t, s, r),
      timings: () => Object.fromEntries(TIMING_KEYS.map((k, i) => [k, +timing[i].toFixed(3)])),
      toggle(name, on) {
        if (name === 'vfx' && vfx) vfx.root.visible = on;
        if (name === 'vignette') vignetteAllowed = on;
        if (name === 'overlay' && vhs) vhs.el.style.display = on ? '' : 'none';
        if (name === 'potatoTarget') post?.potatoViaTarget(on);
        // Cost bisection: hide whole layers.
        if (name === 'map' && mapView) mapView.root.visible = on;
        if (name === 'beans') beansHidden = !on;
        if (name === 'freeze') frozen = on;
        if (name === 'entities' && entities) entities.root.visible = on;
        if (name === 'shadows' && shadows) shadows.mesh.visible = on;
        if (name === 'sun') sun.castShadow = on;
        // 'map:<path>': a part of the map view by name path, e.g. 'map:env' or 'map:env/void' (see
        // `mapParts()`); unnamed parts go by '#<index>'.
        if (name.startsWith('map:') && mapView) {
          let part: Object3D | undefined = mapView.root;
          for (const seg of name.slice(4).split('/')) {
            const kids: Object3D[] = part ? part.children : [];
            part = seg.startsWith('#') ? kids[Number(seg.slice(1))] : kids.find((c) => c.name === seg);
          }
          if (part) part.visible = on;
        }
      },
      mapParts(depth = 1) {
        const out: string[] = [];
        const walk = (o: Object3D, path: string, d: number): void => {
          o.children.forEach((c, i) => {
            const p = `${path}${path ? '/' : ''}${c.name || `#${i}`}`;
            out.push(p);
            if (d > 1) walk(c, p, d - 1);
          });
        };
        if (mapView) walk(mapView.root, '', depth);
        return out;
      },
      tune(name, value) {
        const gl = webgl;
        if (name === 'exposure' && gl) gl.toneMappingExposure = value;
        if (name === 'fog' && scene.fog instanceof FogExp2) scene.fog.density = value;
        if (name === 'hemi') hemi.intensity = value;
        if (name === 'sun') sun.intensity = value;
        if (name === 'toneMapping' && gl) gl.toneMapping = [NeutralToneMapping, ACESFilmicToneMapping, AgXToneMapping][value] ?? NeutralToneMapping;
        const m = /^grade\.(normal|gold|ghost|game)\.(lift|gamma|gain|sat|contrast|tintAmt)$/.exec(name);
        if (m && post) post.tuneGrade(m[1] as 'normal', { [m[2]]: value });
      },
      stress(perSec) {
        stressRate = Math.max(0, perSec);
        stressAcc = 0;
      },
      atlases() {
        const url = (img: unknown): string => (img instanceof HTMLCanvasElement ? img.toDataURL('image/png') : '');
        return { sprites: url(spriteAtlas().image), words: url(popAtlas().image) };
      },
    };
    window.__SHOVEOFF_RENDER__ = hook;
  }

  /** (Re)builds the map view, probe, lights, fog and VFX ground for `info` at the current tier. */
  function buildMap(info: { mapId: MapId; modeId: ModeId; seed: number; golden: boolean }): void {
    const l = need(lib);
    if (geometryTier(tier) !== builtTier) rebuildActors();
    if (mapView) {
      scene.remove(mapView.root);
      mapView.dispose();
    }
    mapView = createMapView(info.mapId, info.seed, l, geometryTier(tier));
    probe = createMapProbe(info.mapId, info.modeId, info.seed);
    solidsOverlay?.setRound(probe);
    scene.add(mapView.root);
    const p = mapView.palette;
    hemi.color.set(p.skyTop);
    hemi.groundColor.set(p.base);
    sun.color.set(p.light);
    scene.fog = new FogExp2(new Color(p.fog), FOG_DENSITY);
    l.setRimColor(p.skyBottom);
    const v = need(vfx);
    v.setPalette(p);
    v.setVoid(mapView.voidKind ?? MAPS[info.mapId].voidKind, p);
    const pr = probe;
    const mv = mapView;
    v.setGround((x, y) => {
      const g = pr.groundAt(x, y);
      // Decals sit on the visual surface (the Teacup saucer's tilt, WP6's surfaceOffset).
      return g > VOID / 2 ? g + (mv.surfaceOffset?.(x, y) ?? 0) : Number.NaN;
    });
    if (lastCards) mapView.setVoteCards?.(lastCards);
    fitShadow();
  }

  /** Recreates the bean rigs and entity views for the current geometry tier. */
  function rebuildActors(): void {
    const l = need(lib);
    for (let slot = 0; slot < rigs.length; slot++) {
      scene.remove(rigs[slot].root);
      rigs[slot].dispose();
      rigs[slot] = createBeanRig(l);
      rigs[slot].setColorblind?.(settings?.accessibility.colorblind ?? false);
      scene.add(rigs[slot].root);
    }
    if (entities) {
      scene.remove(entities.root);
      entities.dispose();
    }
    entities = createEntityViews(l);
    scene.add(entities.root);
    rigLooks.fill(-1);
    builtTier = geometryTier(tier);
  }

  const renderer: GameRenderer = {
    async init(c, s) {
      canvas = c;
      settings = s;
      webgl = new WebGLRenderer({ canvas: c, antialias: false, powerPreference: 'high-performance' });
      webgl.outputColorSpace = SRGBColorSpace;
      webgl.shadowMap.type = PCFShadowMap;
      // Post passes call render() several times a frame: count the whole frame (§12.9 budgets).
      webgl.info.autoReset = false;
      lib = createMaterialLibrary(geometryTier(tier));
      applySettings(s);
      try {
        profileLevel = loadProfile().level;
      } catch {
        profileLevel = 1;
      }
      post = createPostChain(webgl);
      camera = createCameraDirector({
        reducedMotion: () => settings?.gameplay.reducedMotion ?? false,
        shakeScale: () => (settings?.gameplay.screenShake ?? 100) / 100,
        skipIntro: opts.skipIntro,
      });
      camera.setHudInsets(hudInsets);
      for (let slot = 0; slot < MAX_BEANS; slot++) {
        const rig = createBeanRig(lib);
        rig.setColorblind?.(s.accessibility.colorblind);
        rigs.push(rig);
        scene.add(rig.root);
      }
      builtTier = geometryTier(tier);
      vfx = createVfxDirector({
        rigs, camera, post, lib, tier: geometryTier(tier),
        settings: () => ({
          reducedMotion: settings?.gameplay.reducedMotion ?? false,
          reducedFlashing: settings?.gameplay.reducedFlashing ?? false,
          colorblind: settings?.accessibility.colorblind ?? false,
          pushGuide: pushGuideOn(),
        }),
      });
      post.setOverlay(vfx.pops.scene, vfx.pops.camera);
      entities = createEntityViews(lib);
      shadows = createBlobShadows(lib.blobShadowTexture);
      lockerStage = createLockerStage(lib);
      scene.add(hemi, sun, sun.target, entities.root, vfx.root, shadows.mesh);
      if (solidsOverlay) scene.add(solidsOverlay.root);
      sun.shadow.bias = SHADOW.bias;
      sun.shadow.normalBias = SHADOW.normalBias;

      const pmrem = new PMREMGenerator(webgl);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), ENV_SIGMA).texture;
      pmrem.dispose();

      // DOM layers between the canvas and the UI: the replay VHS overlay and Potato's CSS vignette.
      if (c.parentElement) {
        vhs = new VhsOverlay(c.parentElement, c.nextSibling instanceof Element ? c.nextSibling : null);
        vignette = document.createElement('div');
        vignette.className = 'potato-vignette';
        vignette.style.cssText = POTATO_VIGNETTE_CSS;
        vignette.hidden = true;
        c.parentElement.insertBefore(vignette, vhs.el);
      }

      applyTier();
      opts.bus.on('settings', applySettings);
      opts.bus.on('profile', (p) => {
        profileLevel = p.level;
      });
      opts.bus.on('room', (room) => {
        if (room.cards.length === 0 || room.cards === lastCards) return;
        lastCards = room.cards;
        mapView?.setVoteCards?.(room.cards);
      });
      onSim(opts.bus, (ev, local) => {
        vfx?.onSim(ev, local, presentedView);
        mapView?.onEvent(ev, presentedView);
        for (const r of rigs) r.onSim?.(ev, presentedView);   // WP5 hook: honks, emotes, smug after a credited KO
        entities?.onSim?.(ev, presentedView);
      });
      installDebugHook();
    },
    setStage(s) {
      stage = s;
    },
    setRound(info) {
      roundInfo = info;
      buildMap(info);
      need(vfx).reset();
      lift.reset();
      rigLooks.fill(-1);
      governor.reset();
      framesSinceRound = 0;
    },
    render(view) {
      sampleFrame(view);
      applySize();
      const gl = need(webgl);
      gl.info.reset();
      if (stage === 'locker') {
        const ls = need(lockerStage);
        ls.update(view.dt);
        gl.render(ls.scene, ls.camera);
        vhs?.update(null);
        return;
      }
      if (frozen && camera && post) {
        post.render(scene, camera.camera, presentedView);
        return;
      }
      const t0 = performance.now();
      tMark = t0;
      const fxd = need(vfx);
      if (stressRate > 0 && view.source !== 'replay') stressTick(view, fxd);
      const pv = fxd.present(view);
      presentedView = pv;
      if (traceSlot >= 0 && traceRows.length < traceMax) {
        const b = pv.beans[traceSlot];
        const r = view.beans[traceSlot];
        if (b && r) traceRows.push([view.now, b.x, b.y, b.z, b.vx, b.vy, b.vz, fxd.hitstopMs(traceSlot), b.state, b.active ? 1 : 0, r.x, r.y, r.z, view.source === 'replay' ? 1 : 0]);
      }
      if (probe) probe.sync(pv);
      solidsOverlay?.update(pv);
      lap(0);
      mapView?.update(pv);
      // Actors stand on the floor the map view drew (the tilted Teacup saucer, SPUN beans in the cup).
      const lv = lift.apply(pv, mapView?.surfaceOffset, pv.dt);
      presentedView = lv;
      fxd.decals.setSlope(lift.gx, lift.gy);
      lap(1);
      for (let slot = 0; slot < MAX_BEANS; slot++) syncRig(slot, lv);
      lap(2);
      need(entities).update(lv);
      lap(3);
      fxd.update(lv);
      lap(4);
      const cam = need(camera);
      cam.setMoment(fxd.moment);
      cam.update(lv);
      lap(5);
      need(shadows).update(lv, probe, lift.gx, lift.gy);
      lap(6);
      const p = need(post);
      const wipe = replayLook(lv);
      const game = gameSubject(lv);
      postFrame.focus = cam.target;
      postFrame.winnerFeet = game ? winnerFeet : null;
      postFrame.winnerHead = game ? winnerHead : null;
      postFrame.letterbox = fxd.letterbox;
      postFrame.wipe = wipe;
      postFrame.tiltShift = settings?.graphics.tiltShift ?? true;
      p.setFrame(postFrame);
      fxd.beforeRender(cam.camera, lv);
      try {
        p.render(scene, cam.camera, lv);
      } finally {
        fxd.afterRender();
      }
      lap(7);
      timing[8] += (performance.now() - t0 - timing[8]) * 0.05;
      if (vignette) {
        const want = vignetteAllowed && p.tier === 'potato' && !p.finalActive;
        if (vignette.hidden === want) vignette.hidden = !want;
      }
      stats.drawCalls = gl.info.render.calls;
      stats.triangles = gl.info.render.triangles;
      if (mapView) framesSinceRound++;
    },
    setQuality(t) {
      if (t === tier) return;
      tier = t;
      applyTier();
      // Geometry follows the tier (segments, audience, props): rebuild what the old tier built. The
      // round's VFX and the governor's timing carry on.
      if (roundInfo && geometryTier(t) !== builtTier) buildMap(roundInfo);
      opts.bus.emit('quality', t);
    },
    worldToScreen(x, y, z, out) {
      tmpV3.set(x, z, y).project(need(camera).camera);
      out.x = ((tmpV3.x + 1) / 2) * cssW;
      out.y = ((1 - tmpV3.y) / 2) * cssH;
      out.visible = tmpV3.z > -1 && tmpV3.z < 1 && Math.abs(tmpV3.x) <= 1 && Math.abs(tmpV3.y) <= 1;
    },
    groundFromScreen(sx, sy, out) {
      if (cssW === 0 || cssH === 0) return false;
      ndc.set((sx / cssW) * 2 - 1, 1 - (sy / cssH) * 2);
      raycaster.setFromCamera(ndc, need(camera).camera);
      if (!raycaster.ray.intersectPlane(groundPlane, tmpV3)) return false;
      out.x = tmpV3.x;
      out.y = tmpV3.z;
      return true;
    },
    locker: {
      setCosmetics(c, identity) {
        need(lockerStage).setCosmetics(c, identity);
      },
      spin(dx) {
        need(lockerStage).spin(dx);
      },
    },
    renderPortrait(c, identity, size) {
      return need(lockerStage).portrait(need(webgl), c, identity, size);
    },
    stats,
    get framesSinceRound() {
      return framesSinceRound;
    },
    get mapReady() {
      return mapView !== null;
    },
    get vfx() {
      return vfx;
    },
    get cameraDirector() {
      return camera;
    },
    get post() {
      return post;
    },
    setHudInsets(h) {
      hudInsets = h;
      camera?.setHudInsets(h);
    },
    get presented() {
      return presentedView;
    },
    setNameplateRects(rects) {
      vfx?.pops.setPlateRects(rects);
    },
  };
  return renderer;
}
